import { useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { Text, TextInput } from "components";
import { DashboardService } from "services";
import {
  DashboardKpiMeterResponse,
  DashboardKpiParams,
  DashboardScope,
} from "types";

import { DashboardParamsPeriod } from "../shared/dashboardParams";
import SemiGauge from "../shared/SemiGauge";

import classes from "./OperativeSection.module.css";

interface OperativeSectionProps {
  className?: string;
  dateFrom: string;
  dateTo: string;
  onDateFromChange: (value: string) => void;
  onDateToChange: (value: string) => void;
  onPeriodChange: (value: DashboardParamsPeriod) => void;
  params: DashboardKpiParams | null;
  period: DashboardParamsPeriod;
  scope?: DashboardScope;
}

interface OperativeMetric {
  labelKey: string;
  value: number;
  target: number;
  color: string;
}

const DEFAULT_OPERATIVE_METRICS: readonly OperativeMetric[] = [
  {
    labelKey: "dashboard.admin.operative.metrics.leadsInBank",
    value: 40,
    target: 40,
    color: "#eadf53",
  },
  {
    labelKey: "dashboard.admin.operative.metrics.approved",
    value: 4,
    target: 6,
    color: "#be4647",
  },
  {
    labelKey: "dashboard.admin.operative.metrics.disbursed",
    value: 4,
    target: 4,
    color: "#eadf53",
  },
] as const;

const KPI_TYPE_BY_METRIC_KEY = {
  "dashboard.admin.operative.metrics.leadsInBank": "BANK_LEAD",
  "dashboard.admin.operative.metrics.approved": "APPROVED",
  "dashboard.admin.operative.metrics.disbursed": "DISBURSED",
} as const;

const createEmptyMetrics = (): OperativeMetric[] =>
  DEFAULT_OPERATIVE_METRICS.map((metric) => ({
    ...metric,
    value: 0,
    target: 0,
  }));

const mapKpiMetersToMetrics = (
  metrics: readonly OperativeMetric[],
  meters: DashboardKpiMeterResponse[],
): OperativeMetric[] => {
  const metersByType = meters.reduce<Record<string, DashboardKpiMeterResponse>>((acc, item) => {
    acc[item.type] = item;
    return acc;
  }, {});

  return metrics.map((metric) => {
    const metricType = KPI_TYPE_BY_METRIC_KEY[metric.labelKey as keyof typeof KPI_TYPE_BY_METRIC_KEY];
    const data = metricType ? metersByType[metricType] : undefined;

    return {
      ...metric,
      value: typeof data?.value === "number" ? data.value : 0,
      target: typeof data?.target === "number" ? data.target : 0,
    };
  });
};

const OperativeGauge = ({ labelKey, value, target, color }: OperativeMetric) => {
  const { t } = useTranslation();

  return (
    <div className={classes.metric}>
      <SemiGauge
        size="sm"
        value={value}
        target={target}
        color={color}
        className={classes.gauge}
      />

      <Text className={classes.metricLabel}>{t(labelKey)}</Text>
      <Text className={classes.metricValue}>{`${value}/${target}`}</Text>
    </div>
  );
};

const OperativeSection = ({
  className,
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
  onPeriodChange,
  params,
  period,
  scope = "admin",
}: OperativeSectionProps) => {
  const { t } = useTranslation();
  const [metrics, setMetrics] = useState<OperativeMetric[]>(createEmptyMetrics());

  const periodOptions = useMemo(
    () => [
      { value: "DAILY", label: t("dashboard.admin.operative.period.options.daily") },
      { value: "WEEKLY", label: t("dashboard.admin.operative.period.options.weekly") },
      { value: "BIWEEKLY", label: t("dashboard.admin.operative.period.options.biweekly") },
      { value: "MONTHLY", label: t("dashboard.admin.operative.period.options.monthly") },
      { value: "QUARTERLY", label: t("dashboard.admin.operative.period.options.quarterly") },
      { value: "YEARLY", label: t("dashboard.admin.operative.period.options.yearly") },
      { value: "TIME_RANGE", label: t("dashboard.admin.operative.period.options.timeRange") },
    ],
    [t],
  );

  const isTimeRange = period === "TIME_RANGE";

  useEffect(() => {
    let mounted = true;

    if (!params) {
      setMetrics(createEmptyMetrics());
      return () => {
        mounted = false;
      };
    }

    const loadKpis = async () => {
      try {
        const data = await DashboardService.getKpis(params, scope);

        if (!mounted) {
          return;
        }

        setMetrics(mapKpiMetersToMetrics(DEFAULT_OPERATIVE_METRICS, data?.meters ?? []));
      } catch {
        if (!mounted) {
          return;
        }

        setMetrics(createEmptyMetrics());
      }
    };

    loadKpis();

    return () => {
      mounted = false;
    };
  }, [params, scope]);

  return (
    <section className={[classes.root, className].filter(Boolean).join(" ")}>
      <div className={classes.header}>
        <div className={classes.headerMain}>
          <Text className={classes.title}>{t("dashboard.admin.operative.title")}</Text>

          <div className={classes.controls}>
            <select
              className={classes.select}
              value={period}
              onChange={(event) =>
                onPeriodChange((event.currentTarget.value as DashboardParamsPeriod) ?? "YEARLY")
              }
            >
              {periodOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            {isTimeRange ? (
              <div className={classes.dateRange}>
                <Text className={classes.dateLabel}>{t("dashboard.admin.operative.period.from")}</Text>
                <TextInput
                  className={classes.dateInput}
                  placeholder={t("dashboard.admin.operative.period.datePlaceholder")}
                  value={dateFrom}
                  onChange={(event) => onDateFromChange(event.currentTarget.value)}
                  mb={0}
                />

                <Text className={classes.dateLabel}>{t("dashboard.admin.operative.period.to")}</Text>
                <TextInput
                  className={classes.dateInput}
                  placeholder={t("dashboard.admin.operative.period.datePlaceholder")}
                  value={dateTo}
                  onChange={(event) => onDateToChange(event.currentTarget.value)}
                  mb={0}
                />
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className={classes.metrics}>
        {metrics.map((metric) => (
          <OperativeGauge key={metric.labelKey} {...metric} />
        ))}
      </div>
    </section>
  );
};

export default OperativeSection;
